import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  Banknote,
  CircleDollarSign,
  FileText,
  Send,
  Users,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import { money, moneyRounded, parseMoney } from '@/lib/utils';
import { formatNaira } from '@unifemga/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, PageHeader, Skeleton, Stat, Table, Td, Th } from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { LOAN_STATUS } from '@/lib/status';

interface Overview {
  financial: {
    totalLoans: number;
    totalDisbursedFormatted: string;
    totalCollectedFormatted: string;
    totalOutstandingFormatted: string;
    totalOverdueFormatted: string;
    totalOutstanding: string;
    totalOverdue: string;
    collectionRate: number;
  };
  members: { total: number; verified: number; pendingKyc: number; rejectedKyc: number };
  loans: {
    pendingApplications: number;
    pendingDisbursements: number;
    active: number;
    completed: number;
    overdue: number;
    defaulted: number;
  };
  loansByStatus: Array<{ status: string; count: number }>;
  recentLoans: Array<{
    id: string;
    loanNumber: string;
    memberName: string;
    productName: string;
    principal: string;
    outstanding: string;
    status: string;
    daysInArrears: number;
  }>;
}

interface Trend {
  month: string;
  disbursed: string;
  collected: string;
}

const CHART_COLOURS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
];

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.get<Overview>('/dashboard/overview'),
  });

  const { data: trends = [] } = useQuery({
    queryKey: ['admin-trends'],
    queryFn: () => api.get<Trend[]>('/dashboard/trends?months=12'),
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const chartData = trends.map((row) => ({
    month: new Date(`${row.month}-01T00:00:00Z`).toLocaleDateString('en-NG', {
      month: 'short',
      timeZone: 'UTC',
    }),
    // Recharts needs numbers; naira (not kobo) keeps the axis readable, and
    // display precision here is not ledger precision.
    Disbursed: Number(parseMoney(row.disbursed) / 100n),
    Collected: Number(parseMoney(row.collected) / 100n),
  }));

  const statusData = data.loansByStatus.map((row, index) => ({
    name: LOAN_STATUS[row.status as keyof typeof LOAN_STATUS]?.label ?? row.status,
    value: row.count,
    fill: CHART_COLOURS[index % CHART_COLOURS.length],
  }));

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Dashboard"
        description="Operational overview of members, loans and collections."
      />

      {/* Financial */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Total disbursed"
          value={data.financial.totalDisbursedFormatted}
          hint={`${data.financial.totalLoans} loans`}
          icon={<Banknote size={16} />}
        />
        <Stat
          label="Total collected"
          value={data.financial.totalCollectedFormatted}
          hint={`${data.financial.collectionRate.toFixed(1)}% of disbursed`}
          tone="success"
          icon={<CircleDollarSign size={16} />}
        />
        <Stat
          label="Outstanding"
          value={data.financial.totalOutstandingFormatted}
          hint={`${data.loans.active} active loans`}
          icon={<FileText size={16} />}
        />
        <Stat
          label="Overdue"
          value={data.financial.totalOverdueFormatted}
          tone={Number(data.financial.totalOverdue) > 0 ? 'danger' : 'success'}
          hint={`${data.loans.overdue} loans in arrears`}
          icon={<AlertTriangle size={16} />}
        />
      </div>

      {/* A financial dashboard normally shows interest earned here. Ours never
          will, so the space says why rather than leaving an odd gap. */}
      <Card className="mb-4 border-success/20 bg-success-soft/40 p-4">
        <p className="text-sm text-success-soft-foreground">
          <strong className="font-bold">Qard Hasan.</strong> The Foundation earns no interest and charges
          no fees or penalties. Every naira collected is repayment of principal — total collected
          can never exceed total disbursed.
        </p>
      </Card>

      <PlaceholderLimitsWarning />

      {/* Queues */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QueueCard
          to="/admin/applications"
          label="Applications to review"
          count={data.loans.pendingApplications}
          icon={<FileText size={16} />}
        />
        <QueueCard
          to="/admin/disbursements"
          label="Awaiting disbursement"
          count={data.loans.pendingDisbursements}
          icon={<Send size={16} />}
        />
        <QueueCard
          to="/admin/kyc"
          label="KYC to verify"
          count={data.members.pendingKyc}
          icon={<BadgeCheck size={16} />}
        />
        <QueueCard
          to="/admin/members"
          label="Total members"
          count={data.members.total}
          icon={<Users size={16} />}
          muted
        />
      </div>

      {/* Charts */}
      <div className="mb-8 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Disbursements and collections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--foreground-secondary))" />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    stroke="hsl(var(--foreground-secondary))"
                    tickFormatter={(value: number) =>
                      formatNaira(BigInt(Math.round(value)) * 100n, { compact: true })
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      color: 'hsl(var(--popover-foreground))',
                      fontSize: 12,
                    }}
                    formatter={(value: number) =>
                      formatNaira(BigInt(Math.round(value)) * 100n)
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Disbursed" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Collected" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Loans by status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                    {statusData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      color: 'hsl(var(--popover-foreground))',
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent loans */}
      <Card>
        <CardHeader>
          <CardTitle>Recent loans</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <thead>
              <tr>
                <Th>Loan</Th>
                <Th>Member</Th>
                <Th>Product</Th>
                <Th numeric>Principal</Th>
                <Th numeric>Outstanding</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.recentLoans.map((loan) => (
                <tr key={loan.id} className="transition-colors hover:bg-muted/50">
                  <Td>
                    <Link
                      to={`/admin/loans/${loan.id}`}
                      className="numeric font-semibold text-primary hover:underline"
                    >
                      {loan.loanNumber}
                    </Link>
                  </Td>
                  <Td>{loan.memberName}</Td>
                  <Td className="text-foreground-secondary">{loan.productName}</Td>
                  <Td numeric>{money(loan.principal)}</Td>
                  <Td numeric className={loan.daysInArrears > 0 ? 'font-semibold text-danger' : ''}>
                    {money(loan.outstanding)}
                  </Td>
                  <Td>
                    <StatusBadge map={LOAN_STATUS} status={loan.status} />
                  </Td>
                </tr>
              ))}
              {data.recentLoans.length === 0 && (
                <tr>
                  <Td colSpan={6} className="py-10 text-center text-foreground-secondary">
                    No loans yet.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Warns while the seeded loan-product limits are still unapproved.
 *
 * The maximums, terms, guarantor counts and membership requirements were
 * invented to make the system demonstrable. Nobody should mistake them for
 * UNIFEMGA policy, so the warning stays until someone sets
 * `lending.product_limits_approved` to true.
 */
function PlaceholderLimitsWarning() {
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    // A Loan Administrator lacks settings.view, so this 403s for them. That is
    // fine — no retry, and the notice simply does not render.
    queryFn: () => api.get<Array<{ key: string; value: unknown }>>('/settings'),
    retry: false,
  });

  const approve = useMutation({
    mutationFn: () => api.patch('/settings/lending.product_limits_approved', { value: true }),
    onSuccess: () => {
      toast.success('Loan product limits marked as approved.');
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : 'Could not update that setting.'),
  });

  const approved = settings?.find((s) => s.key === 'lending.product_limits_approved')?.value;
  if (approved !== false) return null;

  return (
    <Card className="mb-8 border-warning/30 bg-warning-soft p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <p className="min-w-0 text-sm leading-relaxed text-warning-soft-foreground sm:flex-1">
          <strong className="font-bold">Loan product limits are placeholders.</strong> The maximum
          amounts, repayment terms, guarantor counts and membership requirements currently
          configured were invented for demonstration and have not been approved by the foundation.
        </p>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Link to="/admin/loan-products" className="w-full sm:w-auto">
            <Button size="sm" variant="outline" full>
              Review products
            </Button>
          </Link>
          {/* Actionable where it is read, rather than naming a setting key and
              leaving someone to find the screen. */}
          <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>
            <Check size={14} /> Mark as approved
          </Button>
        </div>
      </div>
    </Card>
  );
}

function QueueCard({
  to,
  label,
  count,
  icon,
  muted,
}: {
  to: string;
  label: string;
  count: number;
  icon: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <Link to={to}>
      <Card className="flex items-center gap-4 p-5 transition-shadow hover:shadow-md">
        <span
          className={
            muted || count === 0
              ? 'flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-foreground-secondary'
              : 'flex h-10 w-10 items-center justify-center rounded-lg bg-warning-soft text-warning'
          }
        >
          {icon}
        </span>
        <div>
          <p className="numeric font-heading text-2xl font-black text-foreground">{count}</p>
          <p className="text-xs font-semibold text-foreground-secondary">{label}</p>
        </div>
      </Card>
    </Link>
  );
}

export { moneyRounded };
